/**
 * useChatPrefs — server-backed chat resume strategy (multi-device).
 *
 * ``device`` — each client keeps its own active session in localStorage.
 * ``latest`` — cold start opens the server-side most recently updated session.
 */
import * as React from "react";
import { getChatPreference, putChatPreference, type ChatPreferenceResponse } from "@/api";
import { useApiClient } from "@/api/useApiClient";
import { useAuth } from "@/auth/useAuth";

export type ChatPrefs = ChatPreferenceResponse;
export type ChatResumeMode = ChatPrefs["resume_mode"];

const DEFAULT_PREFS: ChatPrefs = { resume_mode: "device", updated_at: "" };

export function useChatPrefs() {
  const auth = useAuth();
  const client = useApiClient();
  const clientRef = React.useRef(client);
  clientRef.current = client;
  const [prefs, setPrefs] = React.useState<ChatPrefs>(DEFAULT_PREFS);
  const [loading, setLoading] = React.useState(true);

  /** The saved preference, or the default when it cannot be read: the chat
   *  waits on ``loading``, so a failed read must still finish loading. */
  const fetchPrefs = React.useCallback(async (): Promise<ChatPrefs> => {
    try {
      const { data } = await getChatPreference({ client: clientRef.current, throwOnError: true });
      return data;
    } catch {
      return DEFAULT_PREFS;
    }
  }, []);

  React.useEffect(() => {
    if (!auth.ready || !auth.user) return;
    let cancelled = false;
    void (async () => {
      setLoading(true);
      // fetchPrefs never throws. Before, a network error escaped here and
      // loading stayed true forever — the chat waited on it and never opened.
      const data = await fetchPrefs();
      if (!cancelled) {
        setPrefs(data);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [auth.ready, auth.user, fetchPrefs]);

  const update = React.useCallback(async (resume_mode: ChatResumeMode): Promise<void> => {
    const { data } = await putChatPreference({
      client: clientRef.current,
      body: { resume_mode },
      throwOnError: true,
    });
    setPrefs(data);
  }, []);

  return { prefs, loading, update, refetch: fetchPrefs };
}
